/*
 **	OMNI_IQ (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **	Author(s): Brian Stajkowski brians@silverfallscapital.com
 **
 **/
import React, { useState, useEffect } from "react"
import type {
  ApplicationContextType,
  ApplicationFeatureMap,
  ApplicationFeatureType,
} from "./types"

/* Feature Flags
 *
 * Key in the map is the feature name and the value is the feature object.
 * The feature object contains only the enabled environments, namespaces, and users.
 * If the feature is enabled for all environments, namespaces, or users,
 * the value is null. To disable checking of environments, namespaces, or users,
 * the value is an empty array.
 *
 * Order:
 * 1. Check if the feature is enabled for the user.
 * 2. Check if the feature is enabled for the namespace.
 * 3. Check if the feature is enabled for the environment.
 *
 * All conditions must be true to enable the feature.
 */

export const prioritySeatingFeature: ApplicationFeatureType = {
  featureName: "prioritySeating",
  featureDescription:
    "Priority Seating for the Velolink App using Qstraint devices",
  featureEnvironmentsAllowList: null,
  featureNamespacesAllowList: ["valleytransport"],
  featureUsersAllowList: null,
}

export const featureFlags: ApplicationFeatureMap = {
  prioritySeating: prioritySeatingFeature,
}

/* Feature Flags
 *
 * Component takes in components with feature maps and determines
 * if the component needs to be rendered.
 *
 */

/**
 * Objects contained in allow list or null to enable component
 * @param object
 * @param allowList
 * @returns
 */
function objectContainedInAllowList(
  object: string | undefined,
  allowList: string[] | null,
) {
  if (allowList === null) {
    return true
  }
  if (object === undefined) {
    return false
  }
  return allowList.includes(object.toLowerCase())
}

/**
 * Checks if feature is within the feature flag map
 * @param feature
 * @param map
 * @returns
 */
function featureInMap(feature: string, map: ApplicationFeatureMap) {
  return map[feature] !== undefined
}

/**
 * Feature allowed if all conditions are met
 * @param feature
 * @param appContext
 * @returns true if allowed
 */
export function featureAllowed(
  feature: string,
  appContext: ApplicationContextType,
): boolean {
  if (featureInMap(feature, appContext.features)) {
    const featureMap = appContext.features[feature]
    const user = appContext.userProfile.email
    const namespace = appContext.namespace.namespaceName
    const environment = appContext.environment

    if (
      objectContainedInAllowList(user, featureMap.featureUsersAllowList) &&
      objectContainedInAllowList(
        namespace,
        featureMap.featureNamespacesAllowList,
      ) &&
      objectContainedInAllowList(
        environment,
        featureMap.featureEnvironmentsAllowList,
      )
    ) {
      return true
    }
  }
  return false
}

export default function FeatureFlag({
  featureName,
  baseComponent,
  featureComponent,
  context,
}: {
  featureName: string
  baseComponent: JSX.Element
  featureComponent: JSX.Element
  context: ApplicationContextType
}) {
  const [feature, setFeature] = useState(featureName)
  const [appContext, setAppContext] = useState(context)
  const [enableFeatureComponent, setEnableFeatureComponent] = useState(false)

  useEffect(() => {
    if (featureAllowed(feature, appContext)) {
      setEnableFeatureComponent(true)
    }
  }, [])

  return <>{enableFeatureComponent ? featureComponent : baseComponent}</>
}

export function EmptyFeature() {
  return <></>
}
